"use client";

import React from 'react';
import { Store, Calendar, Clock, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { useTranslation } from 'react-i18next';

interface ReceiptMetadataCardProps {
  storeName: string;
  storeAddress?: string;
  date?: string;
  time?: string;
  nonce: string;
  itemCount?: number;
}

export const ReceiptMetadataCard: React.FC<{ props: ReceiptMetadataCardProps }> = ({ props }) => {
  const { t } = useTranslation();
  const assistantInput = useCartAssistantInput();
  const { messages } = useCartAssistant();
  const { storeName, storeAddress, date, time, nonce, itemCount } = props || {};

  const confirmSignal = `[RECEIPT_METADATA_CONFIRMED]:${nonce}`;

  const [confirmed, setConfirmed] = React.useState(() =>
    messages.some(msg => {
      const content = typeof (msg as any).content === 'string'
        ? (msg as any).content
        : (Array.isArray((msg as any).content) ? (msg as any).content.map((b: any) => b.text || '').join('') : '');
      return content.includes(confirmSignal);
    })
  );
  const [waitingToSubmit, setWaitingToSubmit] = React.useState<string | null>(null);

  // Always-current ref so async retries get the latest submit/setValue, not a stale closure
  const assistantInputRef = React.useRef(assistantInput);
  React.useLayoutEffect(() => {
    assistantInputRef.current = assistantInput;
  });

  // SYNC SUBMISSION LOGIC: Wait for value to be set then fire submit
  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (!waitingToSubmit) return;
      
      const currentInput = assistantInputRef.current;
      
      if (currentInput.value === waitingToSubmit && !currentInput.isDisabled) {
        try {
          console.log(`[CartAssistant] [ReceiptMetadataCard] API SUBMIT INITIATED (isRetry: ${isRetry})`, { nonce });
          await currentInput.submit();
          setWaitingToSubmit(null);
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || err?.status === 400;
          const isInvalidPreviousRun = errorMsg.includes('invalid_previous_run') || errorMsg.includes('Invalid Previous Run');

          console.error(`[CartAssistant] [ReceiptMetadataCard] Submission failed:`, errorMsg);

            if (is400 && isInvalidPreviousRun && !isRetry) {
            console.warn("[CartAssistant] [ReceiptMetadataCard] invalid_previous_run detected. Retrying...");
            try {
              await new Promise(resolve => setTimeout(resolve, 2500));
              assistantInputRef.current.setValue(waitingToSubmit);
              if (!assistantInputRef.current.isDisabled) {
                await assistantInputRef.current.submit();
                setWaitingToSubmit(null);
              }
            } catch (recoveryErr) {
              console.error("[CartAssistant] [ReceiptMetadataCard] Retry failed:", recoveryErr);
              setWaitingToSubmit(null);
            }
          } else {
            setWaitingToSubmit(null);
          }
        }
      }
    };

    fireSubmit();
  }, [waitingToSubmit, assistantInput.value, assistantInput.isDisabled, nonce]);

  const handleConfirm = () => {
    if (confirmed || assistantInput.isDisabled) return;
    setConfirmed(true);
    const msg = `${confirmSignal} Receipt metadata confirmed. Call 'add_all_items_to_basket' with no arguments to add all staged receipt items to the basket now.`;
    assistantInput.setValue(msg);
    setWaitingToSubmit(msg);
  };

  // Format date for display
  const formattedDate = React.useMemo(() => {
    if (!date?.trim()) return null;
    try {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch { return date; }
  }, [date]);

  if (confirmed) {
    return (
      <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100 flex items-center gap-3 animate-in zoom-in-95 duration-300 shadow-sm max-w-xs">
        <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 shrink-0">
          <CheckCircle2 size={18} />
        </div>
        <div>
          <p className="text-xs font-black text-emerald-800 uppercase tracking-widest leading-none">{t('chat.receipt_confirmed')}</p>
          <p className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-tighter mt-0.5">{t('chat.extracting_items')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm max-w-xs overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-3 border-b border-gray-50">
        <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
          <Store size={15} className="text-emerald-600" />
        </div>
        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">{t('chat.receipt_detected')}</p>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        <div>
          <p className="font-black text-gray-900 text-sm leading-tight">{storeName}</p>
          {storeAddress && (
            <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{storeAddress.split(',')[0]}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {formattedDate && (
            <div className="flex items-center gap-1.5">
              <Calendar size={11} className="text-gray-300 shrink-0" />
              <span className="text-[11px] text-gray-500 font-medium">{formattedDate}</span>
            </div>
          )}
          {time?.trim() && (
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="text-gray-300 shrink-0" />
              <span className="text-[11px] text-gray-500 font-medium">{time}</span>
            </div>
          )}
        </div>

        {itemCount != null && itemCount > 0 && (
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
            {t('chat.items_found', { count: itemCount })}
          </p>
        )}
      </div>

      {/* Action */}
      <div className="px-4 pb-4">
        <button
          onClick={handleConfirm}
          disabled={assistantInput.isDisabled}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-wider rounded-xl hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none"
        >
          <span>{t('chat.extract_items')}</span>
          <ChevronRight size={13} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
};
